import { flexRender, getCoreRowModel, getSortedRowModel, useReactTable } from '@tanstack/react-table';
import React, { useState, useEffect, useContext } from 'react';
import { Container, Form, Button, Navbar, Nav } from 'react-bootstrap';
import Common from '../../shared/Common';
import WarningModal from '../../shared/shared-modals/WarningModal';
import type { IIncomeCategory } from '../../../interfaces/IIncomeCategory';
import type { ColumnDef, SortingState } from '@tanstack/react-table';
import { SortDownIcon, SortUpIcon } from '../../shared/icons/Svgs';
import type { IIncomeCategoryData } from '../../../interfaces/IIncomeCategoryData';
import { DictionaryContext } from '../../../App';

export interface ITableProps {
  columns: ColumnDef<IIncomeCategoryData>[];
  data: IIncomeCategoryData[];
  loading: boolean;
  fetchData: () => Promise<void>;
  fetchTrigger: number;
  setShowSpinner: React.Dispatch<React.SetStateAction<boolean>>;
  setFetchTrigger: React.Dispatch<React.SetStateAction<number>>;
  updateIncomeCategoryName: (rowIndex: number, columnId: string, value: any) => void;
  updatedIncomeCategories: IIncomeCategory[];
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  saveButtonDisabled: boolean;
  removeButtonDisabled: boolean;
  setSaveButtonDisabled: React.Dispatch<React.SetStateAction<boolean>>;
  setRemoveButtonDisabled: React.Dispatch<React.SetStateAction<boolean>>;
  selectedIncomeCategory: IIncomeCategoryData | null;
  setSelectedIncomeCategory: React.Dispatch<React.SetStateAction<IIncomeCategoryData | null>>;
}

export interface IEditableCellProps {
  value: any;
  row: any;
  column: any;
  updateCategoryName: (rowIndex: number, columnId: number, value: any) => void;
}

function IncomeCategoriesTable(props: ITableProps) {
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const { reloadIncomeCategories } = useContext(DictionaryContext);

  const defaultColumn: Partial<ColumnDef<IIncomeCategoryData>> = {
    cell: function Cell({ getValue, row, column: { id }, table }) {
      const initialValue = getValue();
      const [value, setValue] = React.useState(initialValue);

      const onCategoryNameChange = (e: any) => {
        if (e.target.value === '') props.setSaveButtonDisabled(true);
        else props.setSaveButtonDisabled(false);
        setValue(e.target.value);
      };

      const onCategoryNameBlur = () => {
        props.updateIncomeCategoryName(row.index, id, value);
      };

      React.useEffect(() => {
        setValue(initialValue);
      }, [initialValue]);

      switch (id) {
        case 'incomeCategoryName':
          if (!row.original.incomeCategory) {
            return <></>;
          }
          return (
            <Form.Control
              type='text'
              onChange={onCategoryNameChange}
              onBlur={onCategoryNameBlur}
              value={value as string}
            />
          );
        case 'totalRevenue':
          return <>{Common.Utils.getNumberWithSpaces(value as string)}</>;
        default:
          return <>{value}</>;
      }
    },
  };

  const table = useReactTable({
    data: props.data,
    columns: props.columns,
    defaultColumn,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const { fetchData, fetchTrigger } = props;

  useEffect(() => {
    fetchData();
  }, [fetchData, fetchTrigger]);

  const saveCategories = async () => {
    props.setShowSpinner(true);
    props.setSaveButtonDisabled(true);
    const requestOptions = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ incomeCategories: props.updatedIncomeCategories }),
    };
    const response = await Common.authorizedFetch('api/categories/saveIncomeCategories', requestOptions);
    const data = await response.json();
    if (!data.success) {
      alert(data.errors);
    } else {
      if (reloadIncomeCategories) {
        reloadIncomeCategories();
      }
      props.setFetchTrigger(props.fetchTrigger + 1);
    }
    props.setShowSpinner(false);
  };

  const onTableRowClick = (incomeCategory: IIncomeCategoryData) => {
    props.setSelectedIncomeCategory(incomeCategory);
    if (incomeCategory.count === 0) {
      props.setRemoveButtonDisabled(false);
    } else {
      props.setRemoveButtonDisabled(true);
    }
  };

  const removeCategory = async () => {
    props.setShowSpinner(true);
    const requestOptions = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        incomeCategoryId: props.selectedIncomeCategory?.incomeCategory?.incomeCategoryId,
      }),
    };
    const response = await Common.authorizedFetch('api/categories/removeIncomeCategory', requestOptions);
    const data = await response.json();
    if (!data.success) {
      alert(data.errors);
    } else {
      if (reloadIncomeCategories) {
        reloadIncomeCategories();
      }
      props.setFetchTrigger(props.fetchTrigger + 1);
      setShowWarningModal(false);
    }
    props.setShowSpinner(false);
  };

  const getClassNameForRow = (rowCategoryId?: number) => {
    if (rowCategoryId === props.selectedIncomeCategory?.incomeCategory?.incomeCategoryId) return 'selected-row';
    return undefined;
  };

  return (
    <>
      <Navbar style={{ backgroundColor: 'transparent' }} variant='light' fixed='bottom'>
        <Container>
          <Nav className='me-auto' />
          <Nav>
            <Button
              disabled={props.removeButtonDisabled}
              className='me-2'
              variant='danger'
              onClick={() => {
                setShowWarningModal(true);
              }}
            >
              Usuń
            </Button>
            <Button
              className='me-2'
              variant='success'
              onClick={() => {
                props.setShowModal(true);
              }}
            >
              Dodaj
            </Button>
            <Button
              disabled={props.saveButtonDisabled}
              className='me-2'
              variant='dark'
              onClick={() => {
                saveCategories();
              }}
            >
              Zapisz
            </Button>
          </Nav>
        </Container>
      </Navbar>
      <div className='mt-1 pb-5'>
        <div className='table-responsive-sm'>
          <table id='income-categories-table' className='table'>
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th key={header.id}>
                      {header.isPlaceholder ? null : (
                        <div
                          {...{
                            style: header.column.getCanSort()
                              ? {
                                  cursor: 'pointer',
                                }
                              : {},
                            onClick: header.column.getToggleSortingHandler(),
                          }}
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {{
                            asc: <SortUpIcon style={{ marginLeft: '5px' }} />,
                            desc: <SortDownIcon style={{ marginLeft: '5px' }} />,
                          }[header.column.getIsSorted() as string] ?? null}
                        </div>
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {props.loading
                ? Common.Ui.getSkeletonTableRows(25, 6, 35)
                : table.getRowModel().rows.map((row) => (
                    <tr
                      key={row.id}
                      className={getClassNameForRow(row.original.incomeCategory?.incomeCategoryId)}
                      onClick={() => {
                        onTableRowClick(row.original);
                      }}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
                      ))}
                    </tr>
                  ))}
            </tbody>
          </table>
        </div>
      </div>
      <WarningModal
        show={showWarningModal}
        modalBodyText={`Czy na pewno chcesz usunąć kategorię przychodu '${props.selectedIncomeCategory?.incomeCategory?.incomeCategoryName}'?`}
        onHide={() => {
          setShowWarningModal(false);
        }}
        onConfirmation={() => removeCategory()}
      />
    </>
  );
}

export default IncomeCategoriesTable;
